import { useCallback, useEffect, useState } from "react";
import api from "../../services/api";
import { getErpPrincipal, temErp } from "../../services/erpSistema";
import { erpInicial, lembrarErp } from "./erpLembrado";

/**
 * ERP escolhido numa tela "por ERP": a tela se comporta como se só ele
 * existisse e envia `erpId` em todas as requisições. Mostre o `ErpFilter`
 * (com `limpavel={false}`) quando `variosErps`.
 *
 * Seleção inicial: HubSoft, se estiver na lista; senão o primeiro. Sem ERP
 * cadastrado (ou em falha), `erpId` fica null e as rotas seguem a variável ERP.
 * Com `lembrar`, começa no ERP escolhido por último numa tela que também
 * lembra, e a escolha fica guardada (components/erp/erpLembrado).
 *
 * @param {string[]} [tipos] Só ERPs desses tipos — para telas que existem só
 *   em alguns ERPs (use `tiposDaTela(rota)` de services/telasErp).
 * @param {boolean} [lembrar] Compartilha a escolha entre as telas.
 * @returns {{ erps: Array|null, erpId: string|null, setErpId: Function,
 *   erp: string, carregado: boolean, variosErps: boolean }}
 *   `erp` é o tipo do ERP escolhido; `carregado` = lista de ERPs já lida
 *   (espere por ele antes da primeira consulta).
 */
export default function useErpSelecionado(tipos, lembrar = false) {
  const [erps, setErps] = useState(null);
  const [erpId, setErpId] = useState(null);

  useEffect(() => {
    let ativo = true;
    api
      .get("/interno/configErp/erpsSistema")
      .then((response) => {
        const lista = (response?.data || []).filter(
          (erp) => !tipos || tipos.includes(erp.tipo),
        );
        const inicial = erpInicial(lista, lembrar);
        if (!ativo) return;
        setErpId(inicial?.id || null);
        setErps(lista);
      })
      .catch((error) => {
        console.error(error);
        if (ativo) setErps([]);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const escolherErp = useCallback(
    (id) => {
      if (lembrar) lembrarErp(id);
      setErpId(id);
    },
    [lembrar],
  );

  // Sem ERP cadastrado: o tipo do sistema (para tela restrita, o primeiro
  // tipo dela que o sistema usa).
  const tipoPadrao = tipos?.length
    ? tipos.find((tipo) => temErp(tipo)) || tipos[0]
    : getErpPrincipal();
  const erp = erps?.find((item) => item.id === erpId)?.tipo || tipoPadrao;

  return {
    erps,
    erpId,
    setErpId: escolherErp,
    erp,
    carregado: erps !== null,
    variosErps: (erps?.length || 0) > 1,
  };
}
